/** Projections over the World registry. */

import { type StopEvent, stopEventsOf } from '../../log/types'
import type { InferenceMode } from '../session/types'

/** One project as a picker sees it, projected out of the World (RFC 0261). */
export interface WorldProjectRef {
  /** The bare doc id, with the `automerge:` key prefix stripped — what a client
   *  actually opens. */
  projectId: string
  displayName: string
  inference: InferenceMode
  /** Whether the deployment's shared worker should serve this project. Read
   *  this rather than comparing `inference` yourself; see `isExternallyServed`. */
  isExternallyServed: boolean
  /** Location in the visible tree. Absent means a legacy top-level project. */
  parent?: { projectId: string; folder: string }
}
/**
 * Does the shared inference worker serve this project?
 *
 * FAILS CLOSED, deliberately, matching searugast: anything that is not exactly
 * `'external'` reads as not-external. Run claims are NOT single-winner across
 * Automerge peers, so an ambiguous value must mean NOBODY serves rather than
 * risking two workers fighting over the same runs. Under-serving is visible and
 * recoverable; double-serving corrupts a session.
 */
export function isExternallyServed(inference: unknown): boolean {
  return inference === 'external'
}
/**
 * Project the World's project registry into a list a picker can render with
 * ZERO project hydration — the reason the registry carries `displayName`.
 *
 * Accepts the raw doc snapshot (as `useDocument(worldId)` hands it over) and
 * returns `[]` for an absent/empty World, so a client that has not created one
 * yet renders empty instead of throwing. Sorted by display name for a stable
 * order across peers — insertion order in a CRDT map is not meaningful.
 */
/** Whether a doc is a World: it has a project registry, `projects`, or
 * `workspaces` before RFC 0357 S1 moves it there on the World's next write. */
export function isWorldDoc(worldDoc: unknown): boolean {
  if (!worldDoc || typeof worldDoc !== 'object') return false
  const world = worldDoc as { projects?: unknown; workspaces?: unknown }
  const projects = world.projects ?? world.workspaces
  return !!projects && typeof projects === 'object'
}

export function projectWorldProjects(worldDoc: unknown): WorldProjectRef[] {
  if (!worldDoc || typeof worldDoc !== 'object') return []
  const world = worldDoc as { projects?: unknown; workspaces?: unknown }
  const projects = world.projects ?? world.workspaces
  if (!projects || typeof projects !== 'object') return []

  const out: WorldProjectRef[] = []
  for (const [key, raw] of Object.entries(
    projects as Record<string, unknown>,
  )) {
    if (!raw || typeof raw !== 'object') continue
    const entry = raw as {
      displayName?: unknown
      inference?: unknown
      parent?: unknown
    }
    const inference = String(entry.inference ?? '') as InferenceMode
    const parent = entry.parent as
      | { projectId?: unknown; folder?: unknown }
      | undefined
    out.push({
      projectId: key.replace(/^automerge:/, ''),
      displayName: String(entry.displayName ?? ''),
      inference,
      isExternallyServed: isExternallyServed(entry.inference),
      ...(parent &&
      typeof parent.projectId === 'string' &&
      typeof parent.folder === 'string'
        ? { parent: { projectId: parent.projectId, folder: parent.folder } }
        : {}),
    })
  }
  return out.sort((a, b) => a.displayName.localeCompare(b.displayName))
}

export interface WorldProjectMount extends WorldProjectRef {
  /** The home project for a top-level project. Null on an unmigrated World. */
  parentProjectId: string | null
  /** One folder name in the parent project. */
  folder: string
}

/** Resolve the World's parent edges without hydrating any project document. */
export function worldProjectMounts(worldDoc: unknown): WorldProjectMount[] {
  const homeProjectId =
    (worldDoc as { homeProjectId?: string } | undefined)?.homeProjectId ?? null
  const projects = projectWorldProjects(worldDoc)
  const byId = new Map(projects.map((project) => [project.projectId, project]))
  const reachesCycle = (id: string, parentId: string): boolean => {
    const visited = new Set([id])
    let current: string | undefined = parentId
    while (current && current !== homeProjectId) {
      if (visited.has(current)) return true
      visited.add(current)
      current = byId.get(current)?.parent?.projectId
    }
    return false
  }
  return projects.map((project) => {
    const parentId = project.parent?.projectId
    const validParent =
      parentId &&
      (parentId === homeProjectId || byId.has(parentId)) &&
      !reachesCycle(project.projectId, parentId)
    return {
      ...project,
      parentProjectId: validParent ? parentId : homeProjectId,
      folder: validParent ? project.parent!.folder : project.displayName,
    }
  })
}
/** One skill as a prompt or a picker sees it, projected out of the World
 *  (RFC 0261 slice 4). */
export interface WorldSkillRef {
  /** The catalog key — stable across renames, and what un-registers it. */
  skillId: string
  title: string
  description: string
  /** The bare document id, `automerge:` stripped: what `read_document` takes. */
  documentId: string
}
/**
 * Project the World's skill catalog. Accepts the raw doc snapshot; returns `[]`
 * for a World with no skills, so every caller can append unconditionally.
 *
 * Skips entries with no link: an entry naming no document is one the agent
 * could be told about but could never read, and a skill it cannot open is worse
 * than a skill it never heard of. Sorted by title for a stable order across
 * peers (map insertion order in a CRDT is not meaningful).
 */
export function projectWorldSkills(worldDoc: unknown): WorldSkillRef[] {
  if (!worldDoc || typeof worldDoc !== 'object') return []
  const skills = (worldDoc as { skills?: unknown }).skills
  if (!skills || typeof skills !== 'object') return []

  const out: WorldSkillRef[] = []
  for (const [skillId, raw] of Object.entries(
    skills as Record<string, unknown>,
  )) {
    if (!raw || typeof raw !== 'object') continue
    const entry = raw as {
      title?: unknown
      description?: unknown
      link?: unknown
    }
    const documentId = String(entry.link ?? '').replace(/^automerge:/, '')
    if (!documentId) continue
    out.push({
      skillId,
      title: String(entry.title ?? skillId),
      description: String(entry.description ?? ''),
      documentId,
    })
  }
  return out.sort((a, b) => a.title.localeCompare(b.title))
}

/**
 * The events the World agent hears from the sessions in its world (RFC 0348
 * 3h), from the World's `worldAgentHears`, flat as `'ended,asked'`. Absent or
 * empty is both.
 */
export function worldAgentHears(worldDoc: unknown): readonly StopEvent[] {
  return stopEventsOf(
    (worldDoc as { worldAgentHears?: unknown } | null | undefined)
      ?.worldAgentHears,
  )
}
